import React, { ComponentPropsWithRef, FC, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { BreadCrumbStep } from '@components/BreadCrumb/BreadCrumb.types';
import { SingleTab } from '@components/Tab/SingleTab.component';
import { TabGroup } from '@components/Tab/TabGroup.component';
import { PlcPageHeader } from '@layout/components/PlcPageHeader/PlcPageHeader.component';
import { PlcPageLayout } from '@layout/components/PlcPageLayout/PlcPageLayout.component';
import { City } from '@meteo/types';
import { NotificationsTabEnum } from '@notifs/business/Notifications.type';
import { WeatherNotifTab } from '@notifs/components/WeatherNotifTab/WeatherNotifTab.component';
import { PlcNotification } from '@notifs/types';
import { useSettings } from '@settings/business/Settings.hook';
import { userSelectors } from '@user/business/User.store';
import { usePlcRouter } from '@utils/customHooks';
import { urlEnum } from '@utils/url';
import classnames from 'classnames';
import { ATLevel2, PlcTagProps } from 'features/tagPlan/business/TagPlan.types';

export interface NotifTabProps {
  defaultTab: NotificationsTabEnum;
  notifications: PlcNotification[];
  favoriteLocalities: City[];
  isPro: boolean;
}

type Props = ComponentPropsWithRef<'div'> & Omit<NotifTabProps, 'isPro'>;

const NotificationsLayout: FC<Props> = ({ defaultTab, notifications, favoriteLocalities, className, ...rest }) => {
  const isPro = useSelector(userSelectors.isPro);
  const { settings, save: saveSettings, saving: savingSettings, error: saveSettingsError } = useSettings();
  const router = usePlcRouter();

  const TABS = [
    {
      content: (
        <WeatherNotifTab
          favoriteLocalities={favoriteLocalities}
          isPro={isPro}
          notifications={notifications}
          saveSettings={saveSettings}
          saveSettingsError={saveSettingsError}
          savingSettings={savingSettings}
          settings={settings}
        />
      ),
      href: NotificationsTabEnum.meteo,
      i18nKey: 'notifications.weather-notif.page-title',
      key: 'forecast',
    },
  ];
  const defaultTabIndex = TABS.findIndex(tab => tab.href === defaultTab);
  const [activeTabIndex, setActiveTabIndex] = useState(defaultTabIndex);

  const handleTabClick = (tabIndex: number) => {
    setActiveTabIndex(tabIndex);

    const page = TABS[tabIndex].href;
    router.replace(`${urlEnum.notifications}/[tab]`, `${urlEnum.notifications}/${page}`, {
      shallow: true,
    });
  };

  const BREADCRUMB_STEPS: BreadCrumbStep[] = [
    {
      label: 'common.newspaper-short',
      linkProps: { href: urlEnum.journal },
    },
    {
      label: TABS[activeTabIndex].i18nKey,
    },
  ];

  const tagProps: PlcTagProps = useMemo(() => {
    const activeTab = TABS[activeTabIndex].href;
    return {
      level2: ATLevel2.UserAccount,
      tagPageNameI18nArray: ['notifications.tag.title', activeTab ? { code: activeTab, notI18n: true } : undefined],
    };
  }, [activeTabIndex]);

  return (
    <PlcPageLayout
      className={classnames('plc-page plc-mb-xxl', className)}
      metaTitleI18n={
        defaultTab === NotificationsTabEnum.meteo ? 'notifications.forecast.title' : 'notifications.newsletter.title'
      }
      tagProps={tagProps}
      {...rest}
    >
      <PlcPageHeader breadcrumbSteps={BREADCRUMB_STEPS} heroImgSrc="notifications" i18nTitle="meteo.my-notifications" />

      <TabGroup activeId={activeTabIndex} setActiveId={handleTabClick}>
        {TABS.map(tab => (
          <SingleTab key={tab.key} label={`notifications.${tab.key}.title`}>
            {tab.content}
          </SingleTab>
        ))}
      </TabGroup>
    </PlcPageLayout>
  );
};

export { NotificationsLayout };
